<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Grid2x2Icon, PenIcon } from '@solar-icons/vue/linear'
import AppCard from '../../primitives/AppCard/AppCard.vue'
import AppBadge from '../../primitives/AppBadge/AppBadge.vue'
import AppSkeleton from '../../primitives/AppSkeleton/AppSkeleton.vue'
import AppSpinner from '../../primitives/AppSpinner/AppSpinner.vue'
import type { BadgeTone } from '../../primitives/AppBadge/badge-types'
import { useDesignRenderUrls } from '../../../composables/useDesignRenderUrls'
import { useDesignStore } from '../../../stores/design'
import { type Design, type DesignRunStatus, isDesignRunLive } from '../../../types/design'

const props = withDefaults(
  defineProps<{
    design: Design
    /** The title of the post the design is the artwork of, `null` for a free design; left out, the card does not say. */
    postTitle?: string | null
  }>(),
  { postTitle: undefined },
)

const emit = defineEmits<{
  open: []
  /** The card was pressed while Raldo is still making the design, so there is nothing to open. */
  making: []
}>()

const { t } = useI18n()
const designStore = useDesignStore()

onMounted(() => {
  designStore.subscribeToDesign(props.design.id)
})

onBeforeUnmount(() => {
  designStore.unsubscribeFromDesign(props.design.id)
})

const latestRun = computed(() => designStore.latestRunFor(props.design.id))
const isMaking = computed(() => isDesignRunLive(latestRun.value))
function onActivate(): void {
  if (isMaking.value) emit('making')
  else emit('open')
}

const runsLoading = computed(() => designStore.isRunsLoading(props.design.id))

const RUN_TONE: Partial<Record<DesignRunStatus, BadgeTone>> = {
  running: 'blue',
  failed: 'red',
}

const runBadge = computed(() => {
  const run = latestRun.value
  const tone = run ? RUN_TONE[run.status] : undefined
  if (!run || !tone) return null
  return { tone, label: t(`design.runBadge.${run.status}`) }
})

const pageCount = ref<number | undefined>(undefined)

async function probeCover(): Promise<void> {
  pageCount.value = undefined
  pageCount.value = await designStore.probePageCount(props.design.id, props.design.renderVersion)
}

watch(() => [props.design.id, props.design.renderVersion], probeCover, { immediate: true })

const designIdRef = computed(() => props.design.id)
const renderVersionRef = computed(() => props.design.renderVersion)
const coverPageCount = computed(() => (pageCount.value && pageCount.value > 0 ? 1 : 0))
const { urls: coverUrls, loading: coverUrlLoading } = useDesignRenderUrls(
  designIdRef,
  renderVersionRef,
  coverPageCount,
)

const coverUrl = computed(() => coverUrls.value[0] ?? null)
const coverLoading = computed(() => pageCount.value === undefined || coverUrlLoading.value)
</script>

<template>
  <AppCard :padding="false" class="min-w-0">
    <button
      type="button"
      class="w-full min-w-0 cursor-pointer text-left outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-blue"
      @click="onActivate"
    >
      <div
        class="relative flex aspect-portrait w-full items-center justify-center overflow-hidden rounded-t-4 bg-bg-tertiary"
      >
        <div
          class="flex size-full items-center justify-center"
          :class="isMaking ? 'blur-scrim' : ''"
        >
          <div v-if="coverLoading || runsLoading" class="size-full">
            <AppSkeleton :rounded="0" />
          </div>
          <img
            v-else-if="coverUrl"
            :src="coverUrl"
            :alt="design.name"
            class="size-full object-cover"
          />
          <Grid2x2Icon v-else class="text-icon-tertiary" />
        </div>
        <p
          v-if="isMaking"
          class="absolute inset-0 grid place-items-center p-8 text-center text-body-sm-medium text-text-primary"
        >
          {{ t('media.designMaking') }}
        </p>
      </div>

      <div class="flex flex-col p-8">
        <p class="truncate text-body-sm-emphasized text-text-primary">{{ design.name }}</p>
        <p
          v-if="postTitle"
          class="icon-sm flex min-w-0 items-center gap-2 text-caption text-text-tertiary"
        >
          <PenIcon class="shrink-0" />
          <span class="truncate">{{ t('media.designOfPost', { title: postTitle }) }}</span>
        </p>
        <p v-else-if="postTitle === null" class="text-caption text-text-tertiary">
          {{ t('media.freeDesign') }}
        </p>
      </div>
    </button>

    <div v-if="runBadge" class="flex items-center gap-4 border-t border-border px-8 py-4">
      <AppSpinner
        v-if="runBadge.tone === 'blue'"
        size="sm"
        tone="current"
        class="text-accent-blue"
      />
      <AppBadge :tone="runBadge.tone">{{ runBadge.label }}</AppBadge>
    </div>
  </AppCard>
</template>
